import { useQuery } from '@tanstack/react-query';
import { fetchTimeToHire } from '@/lib/api';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, TrendingDown, TrendingUp } from 'lucide-react';

interface TimeToHireChartProps {
  startDate?: string;
  endDate?: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: { month: string; averageDays: number; count: number } }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className="rounded-lg border border-white/10 bg-bg-elevated px-3 py-2 shadow-xl">
      <p className="text-sm font-medium text-text-primary">{data.month}</p>
      <p className="text-sm text-emerald-400">{data.averageDays.toFixed(1)} days avg</p>
      <p className="text-xs text-text-tertiary">{data.count} hires</p>
    </div>
  );
}

export function TimeToHireChart({ startDate, endDate }: TimeToHireChartProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'time-to-hire', startDate, endDate],
    queryFn: () => fetchTimeToHire(startDate, endDate),
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5 text-emerald-500" />
            Time to Hire
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-48 bg-white/5 mb-6" />
          <Skeleton className="h-64 w-full bg-white/5" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5 text-emerald-500" />
            Time to Hire
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-text-secondary text-sm">Failed to load time to hire data.</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5 text-emerald-500" />
            Time to Hire
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-text-tertiary">
            <Clock className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">No data available for the selected period.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Determine trend from monthly data
  const months = data.byMonth;
  let trendDirection: 'up' | 'down' | 'neutral' = 'neutral';
  if (months.length >= 2) {
    const last = months[months.length - 1].averageDays;
    const prev = months[months.length - 2].averageDays;
    if (last < prev) trendDirection = 'down';
    else if (last > prev) trendDirection = 'up';
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="h-5 w-5 text-emerald-500" />
          Time to Hire
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Big metric card */}
        <div className="mb-8 rounded-lg border border-white/10 bg-bg-elevated p-6 inline-flex flex-col">
          <span className="text-text-tertiary text-sm font-medium uppercase tracking-wider">
            Average Time to Hire
          </span>
          <div className="flex items-end gap-3 mt-2">
            <span className="text-4xl font-bold text-text-primary tabular-nums">
              {data.averageDays.toFixed(1)}
            </span>
            <span className="text-text-secondary text-lg mb-1">days</span>
            {trendDirection === 'down' && (
              <span className="flex items-center gap-1 text-emerald-400 text-sm mb-1">
                <TrendingDown className="h-4 w-4" />
                Improving
              </span>
            )}
            {trendDirection === 'up' && (
              <span className="flex items-center gap-1 text-red-400 text-sm mb-1">
                <TrendingUp className="h-4 w-4" />
                Increasing
              </span>
            )}
          </div>
        </div>

        {/* Line chart for monthly trend */}
        {months.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={months}
              margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
            >
              <XAxis
                dataKey="month"
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                tickLine={false}
                label={{
                  value: 'Days',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: '#6B7280', fontSize: 12 },
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="averageDays"
                stroke="#10B981"
                strokeWidth={2}
                dot={{ fill: '#10B981', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: '#10B981', stroke: '#fff', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center py-8 text-text-tertiary text-sm">
            No monthly data available to display a trend.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
